import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Ban, Flag, UsersRound } from "lucide-react";

import { useI18n } from "@/i18n/I18nContext";
import { text, contactStateFor } from "@/lib/memberDerive";
import { shortName, interestText, sharedInterests, contactHref } from "@/lib/v3People";
import { useMemberAuth } from "../MemberAuthContext";
import { useCore } from "../useCore";
import { ConnectionError } from "../components/ConnectionError";
import { ReportDialog } from "../components/ReportDialog";
import { Loading, Unavailable } from "../components/kit";
import { V3Title, V3Columns, V3Actions, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { AreaCard, ResponsibleCard, V3_GHOST } from "../components/v3app";
import { ProfileHead } from "../components/v3people";

const ACTION = { none: "Kontakt anfragen", outgoing: "Gesendete Anfrage ansehen", incoming: "Anfrage beantworten", accepted: "Nachricht schreiben" };

// The person as listed by GET /member/directory (Core 1.1 has no detail endpoint, C.3).
export function usePerson(id) {
  const dir = useCore("/member/directory", (d) => (Array.isArray(d.members) ? d.members : []));
  const person = dir.data ? dir.data.find((x) => x && String(x.id) === String(id)) || null : undefined;
  return { dir, person };
}

export function PersonMissing() {
  return (<>
    <V3Title title="Profil nicht verfügbar" testid="person-name" />
    <Unavailable testid="person-not-found">Die Person ist nicht (mehr) auffindbar, nimmt keine Anfragen an oder ihr habt keinen Kontakt mehr.</Unavailable>
    <Link to="/menschen" className={`${V3_SECONDARY} mt-[18px]`} data-testid="person-back">Zu Menschen</Link>
  </>);
}

function PersonView({ m, convs }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const p = useCore("/member/profile", (d) => d.profile || {});
  const [report, setReport] = useState(false);
  const label = (k) => t(`member.categories.${k}`);
  const name = shortName(m.name);
  const first = name.split(" ")[0];
  const { state, conv } = contactStateFor(convs.data || [], m.id);
  const common = p.data ? interestText(sharedInterests(p.data.interests, m.interests), label) : "";
  const go = () => navigate(state === "none" ? `/kontakte/neu/${encodeURIComponent(m.id)}` : contactHref(state, conv.id));
  return (
    <div data-m="M09" data-testid="person-detail">
      <V3Title title="Mitgliedsprofil und Kontakt" intro={`Lerne ${first} über die Angaben kennen, die für Mitglieder sichtbar sind.`} testid="person-title" />
      <V3Columns layout="app" aside={<><AreaCard text={name} testid="person-area" /><ResponsibleCard /></>} main={<>
        <ProfileHead name={name} place={m.city} color={m.avatar_color} testid="person-head" />
        <DetailList>
          <DetailRow label="Name" value={name} testid="person-name" />
          <DetailRow label="Stadt" value={m.city || "Nicht angegeben"} testid="person-city" />
          <DetailRow label="Über mich" value={text(m.bio) || "Nicht angegeben"} testid="person-bio" />
          <DetailRow label="Gemeinsame Interessen" value={!p.data ? "Wird geladen …" : common || "Keine gemeinsamen Interessen angegeben"} testid="person-common" />
        </DetailList>
        {convs.error && <div className="mt-[12px]"><ConnectionError onRetry={convs.reload} /></div>}
        <V3Actions>
          <button type="button" onClick={go} disabled={!convs.data} data-testid={state === "none" ? "person-request" : `person-${state}`} className={V3_PRIMARY}>{ACTION[state] || "Kontaktstatus prüfen"}</button>
          <Link to="/menschen" data-testid="person-back" className={V3_SECONDARY}>Zurück</Link>
        </V3Actions>
        <div className="mt-[16px] flex flex-wrap gap-2">
          <Link to={`/kreise/gemeinsam/${encodeURIComponent(m.id)}`} data-testid="person-shared-groups" className={V3_GHOST}><UsersRound className="h-4 w-4" aria-hidden="true" />Gemeinsame Kreise</Link>
          <Link to={`/kontakte/blockieren/${encodeURIComponent(m.id)}`} data-testid="person-block" className={V3_GHOST}><Ban className="h-4 w-4" aria-hidden="true" />Blockieren</Link>
          <button type="button" onClick={() => setReport(true)} data-testid="person-report" className={V3_GHOST}><Flag className="h-4 w-4" aria-hidden="true" />Melden</button>
        </div>
        <p className="mt-[8px] text-[13px] text-v3-muted">Blockieren und Melden sind getrennte Entscheidungen. Ein Kontakt gibt deinen Access Pass nicht frei.</p>
        <ReportDialog open={report} onOpenChange={setReport} reportedMemberId={m.id} reportedName={name} />
      </>} />
    </div>
  );
}

// M09: only the fields the Core releases for members are shown.
export default function PersonDetail() {
  const { id } = useParams();
  const { member } = useMemberAuth();
  const { dir, person } = usePerson(id);
  const convs = useCore("/member/conversations", (d) => d.conversations || []);
  if (dir.error) return <ConnectionError onRetry={dir.reload} />;
  if (person === undefined) return <Loading />;
  if (!person) return <PersonMissing />;
  return <PersonView key={`${member?.id || "anon"}:${id}`} m={person} convs={convs} />;
}
